import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownSelect = (url: string) => {
    setDropdownOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="kategorien" className="w-full py-16 md:py-20 px-6 md:px-10 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-wider uppercase bg-accent-100 text-accent-800 rounded-full">
            Kategorien
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
            Beliebte OOPBuy Spreadsheet Kategorien
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Wähle eine Kategorie aus und entdecke kuratierte Produkte mit direkten Links zum Spreadsheet
          </p>
        </div>

        <div className="flex justify-center mb-10">
          <div ref={dropdownRef} className="relative w-full max-w-md">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-100 border border-background-300/60 rounded-lg text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium">
                {dropdownOpen ? 'Kategorie wählen...' : 'Kategorie auswählen ▾'}
              </span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line"></i>
              </span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 w-full bg-background-50 border border-background-200/70 rounded-lg overflow-hidden z-30 max-h-72 overflow-y-auto">
                {categories.map((cat, i) => (
                  <button
                    key={i}
                    onClick={() => handleDropdownSelect(cat.url)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors border-b border-background-100 last:border-b-0 cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className={cat.icon}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat, i) => (
            <a
              key={i}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col bg-background-100 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 hover:shadow-sm transition-all duration-200 cursor-pointer"
            >
              <div className="relative w-full aspect-square overflow-hidden bg-background-200/50">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent"></div>
              </div>
              <div className="flex items-center gap-2 px-3 py-3">
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-200 flex-shrink-0">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-foreground-900 whitespace-nowrap">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}